import React from 'react'
import { useSelector } from 'react-redux'

import Button from '@mui/material/Button';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';

import _ from 'lodash';
import { isTrue, describeDate, formatMemberAddress } from '../utils';

const InvoicePdf = ({invoiceId, buttonSize}) => {
	const devMode = window.$umas4m_config.dev_mode;
	
	const invoice = useSelector(state => state.invoices.data[invoiceId]);
	const invoiceInfo = useSelector(state => state.invoices.singles[invoiceId]);
	const invoiceSettings = useSelector(state => state.settings.invoices.data);
	const generalSettings = useSelector(state => state.settings.general.data);
	const member = useSelector(state => state.member.data);
	const logo = useSelector(state => state.requests.logo);
	const pdfFonts = useSelector(state => state.pdfFonts);

	const tableLayouts = {
		u3aLayout: {
			hLineWidth: function (i, node) {
				if(i === 0 ) {
					return 0;
				}
				return (i === 1 || i === node.table.body.length) ? 2 : 1;
			},
			vLineWidth: function (i) {
				return 0;
			},
			hLineColor: function (i) {
				return '#2a55a0';
			},
			paddingLeft: function (i) {
				return 4;
			},
			paddingRight: function (i) {
				return 4;
			},
			paddingTop: function (i) {
				return 8;
			},
			paddingBottom: function (i) {
				return 8;
			}
		},
		totalsLayout: {
			hLineWidth: function (i, node) {
				if(i === 0 ) {
					return 0;
				} 
				return (i === 1) ? 1 : 2;
			},
			vLineWidth: function (i) {
				return 0;
			},
			hLineColor: function (i) {
				return '#2a55a0';
			},
			paddingLeft: function (i) {
				return 4;
			},
			paddingRight: function (i) {
				return 4;
			},
			paddingTop: function (i) {
				return 8;
			},
			paddingBottom: function (i) {
				return 8;
			}
		}
	}
	
	const docDefinition = {
		pageSize: 'A4',
		pageMargins: [28, 42, 28, 72],
		defaultStyle: {
			fontSize: 11,
		},
		styles: {
			header: {
				fontSize: 28,
				bold: true,
				margin: [0, 0, 0, 24]
			},
			tripleParagraph: {
				margin: [0, 0, 0, 24]
			},
			doubleParagraph: {
				margin: [0, 0, 0, 16]
			},
			paragraph: {
				margin: [0, 0, 0, 8]
			},
			th: {
				bold: true,
				fillColor: '#ffe793',
			},
			waitlist: {
				decoration: 'lineThrough',
				color: '#aaaaaa',
				alignment: 'right'
			},
			numCol: {
				alignment: 'right'
			},
			tfoot: {
				fillColor: '#fffaea'
			}
		}
	}
	
	const getPaymentDetail = () => {
		if(invoice.due <= 0){
			return '';
		}
		let columns = [];
		
		if(isTrue(invoiceSettings.bank.accept) && (!_.isEmpty(invoiceSettings.bank.detail) || invoiceSettings.bank.use_payid)){
			let bankDetails = [{text: 'Bank transfer', bold: true, style: 'paragraph', fontSize: 12}];
			
			if(invoiceSettings.bank.use_payid){
				bankDetails.push(invoiceSettings.bank.payid_detail);
				bankDetails.push({text: 'Use reference: ' + invoice.member_no + '-' + invoice.invoice_no, style: 'paragraph'});
				bankDetails.push({text: 'PayID is safer than BSB/account number. Check with your financial institution to find out how to use PayID.', style: 'doubleParagraph'});
			} else {
				bankDetails.push(invoiceSettings.bank.detail);
				bankDetails.push({text: 'Use reference: ' + invoice.member_no + '-' + invoice.invoice_no, style: 'doubleParagraph'});
			}
			
			columns.push({
				width: '30%',
				stack: bankDetails
			});
		}
		
		if(isTrue(invoiceSettings.paypal.accept)){
			columns.push({
				width: '30%',
				stack: [
					{text: 'PayPal or credit card', bold: true, style: 'paragraph', fontSize: 12},
					{text: 'Pay with PayPal or credit card from the My Invoices page when you are logged in to U-MAS. For detailed instructions, click the Help button near the top right of the My Invoices page.', style: 'doubleParagraph'}
					
				]
			});
		}
		
		let accept_cheques = false;
		let other_methods = [];
		if(invoiceSettings.accept_eftpos_at_office){
			other_methods.push({ text: 'EFTPOS at the office'})
		}
		if(invoiceSettings.accept_cheques_at_office){
			other_methods.push({ text: 'Cheque at the office'});
			accept_cheques = true;
		}
		if(invoiceSettings.accept_cash_at_office){
			other_methods.push({ text: 'Cash at the office'});
		}
		if(invoiceSettings.accept_cheques_via_post){
			other_methods.push({ text: 'Cheque by mail, and sent to:\r\n' + generalSettings.formal_name + '\r\n' + invoiceSettings.postal_address});
			accept_cheques = true;
		}
		
		if(!_.isEmpty(other_methods)){
			columns.push({
				width: '*',
				stack: [
					{text: 'Other payment methods', bold: true, style: 'paragraph', fontSize: 12},
					{ul: other_methods, style: accept_cheques ? 'paragraph' : null},
					accept_cheques ? {text: 'If paying by cheque, please make it out to ' + invoiceSettings.make_cheques_out_to + ', and write Invoice ID ' + invoice.member_no + '-' + invoice.invoice_no + ' on the back', bold: true, style: 'paragraph'} : null
				]
			})
		}
		
		if(columns.length === 0){
			columns.push({
				width: '100%',
				stack: [
					{text: 'Payment', bold: true, style: 'paragraph'},
					{text: 'Please contact us to find out how to pay this invoice.', style: 'doubleParagraph'}
				]
			});
		}
		
		return { columns: columns, columnGap: 10 };
	}

	const downloadPdf = () => {
		const lineItems = _.map(invoice.line_items, i => {
			return [
				i.course_code,
				i.name,
				describeDate(i.start_date),
				i.status,
				i.status === 'Waitlist' ? {text: '$'+i.amount, style: 'waitlist'} : {text: '$'+i.amount, style: 'numCol' }
			];
		});
		
		lineItems.unshift([
			{text: 'Code', style: 'th' },
			{text: 'Name', style: 'th' },
			{text: 'Start date', style: 'th' },
			{text: 'Status', style: 'th' },
			{text: 'Fee', style: 'th', alignment: 'right' }
		]);
		
		/*
		Request by Andrew and Kevin to remove notes about waitlist. 
		Commenting out for now: Delete in future version,
		assuming we don't get complaints and get asked to put it back in!
		const waitlistNote = _.some(invoice.line_items, item => {
			return item.status === 'Waitlist' && _.toNumber(item.amount) > 0;
		}) ? 'Waitlist enrolments are not charged. If a waitlist enrolment is later converted to a full enrolment, your invoice will be updated at that time. ' : '';
		
		const deletedNote = _.some(invoice.line_items, item => {
			return item.status === 'Deleted' && _.toNumber(item.amount) > 0
		}) ? 'Deleted enrolments are still charged.' : '';
		*/
		
		const paymentDetail = getPaymentDetail();
		
		const u3aLogo = logo.image ? {
			image: logo.image,
			fit: [210, 50]
		} : {
			width: '*',
			text: '[ '+generalSettings.display_name+' logo ]',
			italics: true,
		};

		const invoiceDefinition = {...docDefinition, content: [
			{
				columns: [
					{
						width: '60%',
						text: invoiceSettings.use_tax_invoices ? 'TAX INVOICE' : 'INVOICE', 
						style: 'header'
					}, 
					u3aLogo
				],
				columnnGap: 10
			},
			{
				columns: [
					{
						width: '60%',
						stack: [
							{ 
								text: [
									{text: 'Invoice Id: ', bold: true },
									invoice.member_no + '-' + invoiceId
								], 
								style: 'paragraph',
								fontSize: 12
							},
							{ 
								text: [
									{text: 'Date: ', bold: true },
									describeDate(invoice.date)
								], 
								style: 'doubleParagraph',
								fontSize: 12
							},
							{text: 'Bill to: ', bold: true},
							{text: member.full_name, fontSize: 12},
							{text: 'Member no: '+ member.member_no, style: 'paragraph', fontSize: 12},
							{text: formatMemberAddress(member), style: 'paragraph', fontSize: 12},
							{text: member.email ? 'E: ' + member.email.trim() : '', fontSize: 12},
							{text: member.mobile ? 'Mob: ' + member.mobile : '', style: 'doubleParagraph', fontSize: 12}
						]
					}, 
					{
						width: '*',
						stack: [
							{text: generalSettings.formal_name, bold: true, style: 'paragraph', fontSize: 12 },
							{text: 'Incorporation: '+ invoiceSettings.incorporation, style: 'paragraph', fontSize: 12},
							{text: 'ABN: ' + invoiceSettings.abn, style: 'paragraph', fontSize: 12},
							{text:invoiceSettings.address, style: 'paragraph', fontSize: 12},
							{text: 'Postal address:', bold: true, fontSize: 12},
							{text:invoiceSettings.postal_address, style: 'tripleParagraph', fontSize: 12}
						]
					}
				],
				columnnGap: 10
			},
			{
				layout: 'u3aLayout',
				table: {
					headerRows: 1,
					widths: ['*', '30%', '*', '10%', '10%'],
					body: lineItems
				}
			},
			{
				layout: 'totalsLayout',
				style: 'doubleParagraph',
				table: {
					headerRows: 0,
					widths: ['60%','30%', '10%'],
					body: [
						[
							'',
							{text: invoiceSettings.use_tax_invoices ? 'Total price including GST' : 'Total', style: 'numCol'},
							{text: '$' + invoice.total, style: 'numCol'}
						],
						[
							'',
							{text: 'Paid', style: 'numCol'},
							{text: '$' + invoice.paid, style: 'numCol'}
						],
						[
							{text: '', style: 'tfoot'},
							{text: 'Due', style: 'tfoot', bold: true, alignment: 'right'},
							{text: '$' + invoice.due, style: 'tfoot', bold: true, alignment: 'right'},
						]
					]
				}
			},
			paymentDetail
		]};
		
		/*
		pdfMake increases bundle size ridiculously, 
		so split it out and only load if we need it
		*/
		import('pdfmake/build/pdfmake').then(pdfmake => {
			
			if(devMode){
				pdfmake.createPdf(invoiceDefinition, tableLayouts, pdfFonts).open();
			} else {
				pdfmake.createPdf(invoiceDefinition, tableLayouts, pdfFonts).download('Invoice_'+member.member_no+'-'+invoiceId+'.pdf');
			}
		})

	}

	if(_.isNil(invoiceInfo) || _.isNil(invoice) || invoiceInfo.submitting || invoiceInfo.error ){
		return <></>
	} else {
		return <Button color="primary" size={buttonSize}
			startIcon={<DescriptionOutlinedIcon />}
			onClick={(e) => downloadPdf()}
		>{buttonSize === 'small' ? 'PDF' : 'Download PDF'}</Button>
	}
}
export default InvoicePdf